import React, {
  useEffect,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from 'react'
import moment from 'moment'
import { useCandidateChat } from '../CandidateChatContext'
import { Typography } from '../../../_atoms'
import AttachmentDisplay from './AttachmentDisplay'
import { useTranslation } from '~/hooks/useTranslation'

/**
 * TextMessages component - displays chat messages in a scrollable container
 * Based on the op_next design from app/views/home/coach_chat/content/_text_messages.html.erb
 *
 * @param {Object} props
 * @param {Array} props.messages - Array of chat messages
 * @param {string} props.chatStatus - Current chat status
 * @param {Object} props.selectedCoach - The currently selected coach
 * @param {React.ReactNode} props.footer - Optional footer content to display after messages
 * @param {boolean} props.scrollToBottomOnMount - Whether to scroll to bottom when component mounts
 */
const TextMessages = forwardRef(
  (
    { messages, footer, scrollToBottomOnMount = false, isWideFrame = false },
    ref,
  ) => {
    const messagesEndRef = useRef(null)
    const isInitialLoadRef = useRef(true)
    const typingTimeoutRef = useRef(null)
    const [isCoachTyping, setIsCoachTyping] = useState(false)
    const {
      selectedCoach: contextSelectedCoach,
      candidateChatManager,
      getDarkModeClasses,
    } = useCandidateChat()
    const { t } = useTranslation('coach_chat')

    // Expose scrollToBottom method via ref
    useImperativeHandle(ref, () => ({
      scrollToBottom: () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
      },
    }))

    // Auto-scroll to bottom when new messages arrive
    useEffect(() => {
      // Skip auto-scroll on initial load unless scrollToBottomOnMount is true
      if (isInitialLoadRef.current) {
        isInitialLoadRef.current = false
        if (scrollToBottomOnMount) {
          // Use setTimeout to ensure DOM is rendered
          setTimeout(() => {
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
          }, 100)
        }
        return
      }

      // Only auto-scroll if there are actual messages
      if (messages && messages.length > 0) {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
      }
    }, [messages, scrollToBottomOnMount])

    // Listen for coach typing indicators
    useEffect(() => {
      const handleCoachTyping = () => {
        // Clear any existing timeout to prevent weird display when events trickle in
        if (typingTimeoutRef.current) {
          clearTimeout(typingTimeoutRef.current)
        }

        setIsCoachTyping(true)

        // Clear typing indicator after 10 seconds
        typingTimeoutRef.current = setTimeout(() => {
          setIsCoachTyping(false)
          typingTimeoutRef.current = null
        }, 10000)
      }

      // Listen for messages to clear typing indicator immediately
      const handleMessageReceived = () => {
        if (typingTimeoutRef.current) {
          clearTimeout(typingTimeoutRef.current)
          typingTimeoutRef.current = null
        }
        setIsCoachTyping(false)
      }

      if (candidateChatManager) {
        candidateChatManager.addEventListener('coachTyping', handleCoachTyping)
        candidateChatManager.addEventListener(
          'peerMessage',
          handleMessageReceived,
        )
        return () => {
          // Clean up timeout on unmount
          if (typingTimeoutRef.current) {
            clearTimeout(typingTimeoutRef.current)
          }
          candidateChatManager.removeEventListener(
            'coachTyping',
            handleCoachTyping,
          )
          candidateChatManager.removeEventListener(
            'peerMessage',
            handleMessageReceived,
          )
        }
      }
    }, [candidateChatManager])

    // Auto-scroll to bottom when coach typing indicator appears
    useEffect(() => {
      if (isCoachTyping) {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
      }
    }, [isCoachTyping])

    // Helper function to format message timestamp
    const formatTimestamp = (timestamp) => {
      if (!timestamp) return ''
      return moment(timestamp).format('LT') // Local time format (e.g., "2:30 PM")
    }

    // Helper function to render message content safely
    const renderMessageContent = (content) => {
      if (!content) return ''

      // Check if this is a "sent {filename}" message and apply ellipsis to filename
      const sentFileMatch = content.match(/^(sent\s+)(.+)$/i)
      if (sentFileMatch) {
        const [, prefix, filename] = sentFileMatch
        // Escape HTML
        const escapedPrefix = prefix
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
        const escapedFilename = filename
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')

        return (
          <span>
            {escapedPrefix}
            <span
              className="inline-block max-w-[200px] truncate align-bottom"
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={filename}
            >
              {escapedFilename}
            </span>
          </span>
        )
      }

      // Escape HTML and convert line breaks to <br> tags
      const escapedContent = content
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/\n/g, '<br />')

      return <span dangerouslySetInnerHTML={{ __html: escapedContent }} />
    }

    // Helper function to get sender name
    const getSenderName = (message) => {
      if (
        message.sender === 'user' ||
        message.sender === 'me' ||
        message.sender === 'candidate'
      ) {
        return 'Me'
      }
      if (message.sender === 'coach') {
        // Use senderName from message if available, otherwise fall back to context or default
        if (message.senderName) {
          return message.senderName
        }
        // Try to get coach name from context if fromEasyrtcid matches
        if (
          contextSelectedCoach &&
          message.fromEasyrtcid === contextSelectedCoach.easyrtcid
        ) {
          return (
            contextSelectedCoach.firstName ||
            contextSelectedCoach.displayName ||
            'Coach'
          )
        }
        return 'Coach'
      }
      return message.senderName || message.sender || 'Unknown'
    }

    // Helper function to force download of file
    const handleDownload = async (downloadUrl, fileName) => {
      if (!downloadUrl) return

      try {
        // Fetch the file
        const response = await fetch(downloadUrl)
        if (!response.ok) {
          throw new Error('Failed to fetch file')
        }

        // Get the blob
        const blob = await response.blob()

        // Create a blob URL
        const blobUrl = window.URL.createObjectURL(blob)

        // Create a temporary anchor element and trigger download
        const link = document.createElement('a')
        link.href = blobUrl
        link.download = fileName || 'download'
        document.body.appendChild(link)
        link.click()

        // Clean up
        document.body.removeChild(link)
        window.URL.revokeObjectURL(blobUrl)
      } catch (error) {
        console.error('Error downloading file:', error)
        // Fallback to opening in new tab if download fails
        window.open(downloadUrl, '_blank')
      }
    }

    // Helper function to render coach_action messages
    const renderCoachAction = (message) => {
      const actionType = message.actionType || message.actionData?.action_type
      const actionData = message.actionData || {}

      // Special handling for chat_attachment
      if (actionType === 'chat_attachment') {
        const fileName =
          actionData.attachment_name || message.attachment?.name || 'file'
        const downloadUrl = actionData.action_target || message.attachment?.url
        const senderName = getSenderName(message)

        return (
          <div className="message-action">
            <div className="font-roboto text-base font-normal leading-relaxed">
              <span>
                {senderName} {t('sent_file', 'sent')}{' '}
              </span>
              {downloadUrl ? (
                <button
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    handleDownload(downloadUrl, fileName)
                  }}
                  className="inline-block max-w-full !cursor-pointer truncate text-white !underline"
                  style={{
                    maxWidth: '200px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    verticalAlign: 'bottom',
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    textAlign: 'left',
                  }}
                  title={fileName}
                >
                  {fileName}
                </button>
              ) : (
                <span
                  className="inline-block max-w-full truncate"
                  style={{
                    maxWidth: '200px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    verticalAlign: 'bottom',
                  }}
                  title={fileName}
                >
                  {fileName}
                </span>
              )}{' '}
              <span>
                {t('at', 'at')}{' '}
                {formatTimestamp(message.ts || message.timestamp)}
              </span>
            </div>
          </div>
        )
      }

      // Handle other action types
      let actionText = ''
      switch (actionType) {
        case 'navigate':
          actionText = `${t('navigated_to', 'navigated to')} ${actionData.action_target?.pathname || actionData.action_target || ''}`
          break
        case 'started_chat_from':
          actionText = `${t('started_chat_from', 'started chat from')} ${actionData.action_target || ''}`
          break
        case 'left_chat':
          actionText = t('left_chat', 'left chat')
          break
        case 'pause':
          actionText = actionData.paused
            ? t('paused', 'paused their camera')
            : t('unpaused', 'unpaused their camera')
          break
        case 'mute':
          actionText = actionData.paused
            ? t('muted', 'muted their audio')
            : t('unmuted', 'unmuted their audio')
          break
        case 'ended_chat':
          actionText = t('coach_ended_chat', 'ended the chat')
          break
        default:
          actionText = t('chat_attachment', 'uploaded a file')
      }

      const senderName = getSenderName(message)
      return (
        <div className="message-action">
          <div className="font-roboto text-base font-normal leading-relaxed">
            {senderName} {actionText} {t('at', 'at')}{' '}
            {formatTimestamp(message.ts || message.timestamp)}
          </div>
        </div>
      )
    }

    return (
      <div
        className={`text-messages relative flex h-full flex-col overflow-hidden ${
          isWideFrame ? 'text-messages--wide' : ''
        }`}
      >
        <div
          className={`text-messages-content flex-1 overflow-y-auto px-5 py-4 ${
            isWideFrame ? 'min-h-[8rem]' : ''
          }`}
        >
          {/* Render existing messages */}
          {messages &&
            messages.map((message, index) => {
              const senderName = getSenderName(message)
              const isOwnMessage = senderName.toLowerCase() === 'me'

              // Check if message is a coach_action
              const isCoachAction = message.type === 'coach_action'

              // For coach_action messages, render them differently
              if (isCoachAction) {
                return (
                  <div
                    key={message.id || index}
                    className="mb-4 flex justify-start"
                  >
                    <div className="max-w-[90%] rounded-r-xl rounded-t-xl bg-intoo-green-medium-800 px-4 py-3 font-roboto text-base leading-relaxed text-white">
                      {renderCoachAction(message)}
                    </div>
                  </div>
                )
              }

              return (
                <div
                  key={message.id || index}
                  className={`mb-4 flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[90%] px-4 py-3 font-roboto text-base leading-relaxed ${
                      isOwnMessage
                        ? getDarkModeClasses(
                            'rounded-l-xl rounded-t-xl bg-cod-gray-100 text-cod-gray-900',
                            'rounded-l-xl rounded-t-xl bg-gray-600 text-white',
                          )
                        : 'rounded-r-xl rounded-t-xl bg-intoo-green-medium-800 text-white'
                    }`}
                  >
                    {/* Render attachment if present */}
                    {message.attachment && (
                      <AttachmentDisplay
                        attachment={message.attachment}
                        isOwnMessage={isOwnMessage}
                        getDarkModeClasses={getDarkModeClasses}
                      />
                    )}

                    {/* Render text content if present */}
                    {message.content && (
                      <Typography
                        variant="body"
                        className={
                          isOwnMessage
                            ? getDarkModeClasses(
                                'text-cod-gray-900 [&_a]:text-cod-gray-900',
                                'text-white [&_a]:text-white',
                              )
                            : 'text-white [&_a]:!cursor-pointer [&_a]:text-white [&_a]:!underline'
                        }
                      >
                        {renderMessageContent(message.content)}
                      </Typography>
                    )}

                    <Typography
                      variant="small"
                      className={`mt-1 ${
                        isOwnMessage
                          ? getDarkModeClasses('text-gray-500', 'text-gray-300')
                          : 'text-green-100'
                      }`}
                    >
                      {formatTimestamp(message.timestamp)}
                    </Typography>
                  </div>
                </div>
              )
            })}

          {/* Footer content */}
          {footer && <div className="footer-content">{footer}</div>}

          {/* Invisible element for auto-scrolling */}
          <div ref={messagesEndRef} />
        </div>

        {/* Show typing indicator when coach is typing - absolute positioned bar at bottom */}
        {isCoachTyping && (
          <div
            className={`absolute bottom-0 left-0 right-0 z-10 px-5 py-2 ${getDarkModeClasses(
              'bg-gray-100/80 backdrop-blur-sm',
              'bg-gray-800/80 backdrop-blur-sm',
            )}`}
          >
            <Typography
              variant="small"
              className={getDarkModeClasses('text-gray-700', 'text-gray-300')}
            >
              {contextSelectedCoach?.firstName || 'Coach'} is typing...
            </Typography>
          </div>
        )}
      </div>
    )
  },
)

TextMessages.displayName = 'TextMessages'

export default TextMessages
